import React, { FC, ReactElement, useMemo, useState } from 'react';
import PageSection from '../../HOC/PageSection/PageSection';
import { useFilters } from '../../hooks/useFilters';
import { useLocalSort } from '../../hooks/useLocalSort';
import { ICategoryResult } from '../../services/reports';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import TogglersEntity from '../TogglersEntity/TogglersEntity';
import {
  Disagreement,
  HighAgreement,
  LowAgreement,
  SomeAgreement,
} from './AgreementLegends/AgreementLegends';
import TopicLevelChart from './TopicLevelChart/TopicLevelChart';
import {
  Header,
  Container,
  GroupCont,
  AgreementsContainer,
} from './TopicLevelWorkEnvironment.styles';

interface TopicLevelWorkEnvironmentProps {
  sectionTitle: ReactElement;
  data: ICategoryResult[];
  isExecutiveReport: boolean;
  description: string;
}

const reportKey = 'topicLevelReport';

const TopicLevelWorkEnvironment: FC<TopicLevelWorkEnvironmentProps> = ({
  sectionTitle,
  data,
  description,
}) => {
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { loading } = useLocalSort([...data], reportKey, 'categorySort');

  const [togglersState, setTogglersState] = useState<{
    historical_result: boolean;
    agreement: boolean;
  }>({
    historical_result: false,
    agreement: false,
  });

  const getCategoriesFilters = useMemo(() => {
    const categories = currentFilters?.surveyReport;

    if (categories) {
      // @ts-expect-error query-string types a value as string | string[]
      const catsFilters = JSON.parse(categories);

      return {
        legend: catsFilters?.legend || false,
        mode: catsFilters?.mode || 'totals',
      };
    }
  }, [currentFilters?.surveyReport]);

  const [localLegendExpanded, setLocalLegendExpanded] = useState<boolean>(
    getCategoriesFilters?.legend || false
  );

  const handleExpandLegend = (status: boolean) => {
    setLocalLegendExpanded(status);
    applyAndMergeFilters(reportKey, 'legend', status);
  };

  const handleToggleState = (name: 'historical_result' | 'agreement') => {
    setTogglersState(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const isHistoricalResults = Object.keys(data[0].benchmarks).length !== 0;
  const isAgreementActive = togglersState.agreement;

  return (
    <>
      <Header>{sectionTitle}</Header>
      <PageSection>
        <Container>
          <ReportResultsLegend
            noBorder
            description={description}
            variant="rotated"
            legendExpanded={localLegendExpanded}
            expandLegend={handleExpandLegend}
          >
            <TogglersEntity
              handleToggleState={handleToggleState}
              isHistoricalResults={isHistoricalResults}
            />
          </ReportResultsLegend>
          <GroupCont id="GroupCont">
            {loading ? (
              <div>loading</div>
            ) : (
              <TopicLevelChart data={data} togglersState={togglersState} />
            )}
          </GroupCont>
          {isAgreementActive && (
            <AgreementsContainer>
              <HighAgreement />
              <SomeAgreement />
              <LowAgreement />
              <Disagreement />
            </AgreementsContainer>
          )}
        </Container>
      </PageSection>
    </>
  );
};

export default TopicLevelWorkEnvironment;
